import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  FlatList,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { snapPoint } from 'react-native-redash';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AvatarProps } from '@components/Avatar/Avatar';
import MemberListSummary from '@components/MemberListSummary/MemberListSummary';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';
import { Tag } from '@components/shared/Tags/Tag';
import ProjectOverlay from '@components/Tutorialization/ProjectOverlay';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import { User } from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import { useChatTutorial } from '@hooks/useChatTutorial';
import Images from '@themes/images';
import theme from '@themes/theme';

export type ModalItemType = {
  title: string;
  iconName?: keyof typeof Images;
  label?: string;
  onSelect: () => void;
  color?: keyof typeof theme.colors;
  titleColor?: keyof typeof theme.colors;
  comingSoon?: boolean;
  disable?: boolean;
  accessibilityLabel?: string;
  trailingIconName?: keyof typeof Images;
  users?: User[];
  /**
   * Shows a spinner in place of this row's icon and locks every row, the
   * backdrop and the swipe until it clears. For options that keep the sheet
   * open while async work runs, so the row cannot be tapped a second time and
   * the wait is visible rather than a dead tap (TA-10731).
   */
  isLoading?: boolean;
};

interface ModalProps {
  items?: ModalItemType[];
  goBack?: () => void;
  gestureDisabled?: boolean;
  paddingDisabled?: boolean;
  children?: JSX.Element;
  showHeader?: boolean;
  headerTitle?: string;
  headerSaveDisable?: boolean;
  onSave?: () => void;
  cancelLabel?: string;
  showSaveButton?: boolean;
  closeOnTap?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  items,
  goBack,
  children,
  gestureDisabled,
  paddingDisabled,
  showHeader,
  headerTitle,
  onSave,
  headerSaveDisable,
  showSaveButton = true,
  cancelLabel,
  closeOnTap = true,
}) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const { t } = useTranslation();

  const [height, setHeight] = useState<number>(0);
  const { setModalHeight } = useChatInput();

  const { isVisibleForChat, chatTutorialState } = useChatTutorial();

  // Declared above `pan` on purpose: the gesture reads `showOverlay` during
  // render, and while this was declared further down the file it compiled to
  // a hoisted `var` that was still `undefined` at that point — so
  // `.enabled(...)` was permanently true and the tutorial overlay never
  // actually blocked the swipe (TA-10731).
  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  // An option is running async work with the sheet still open. Everything that
  // could start a second run or pull the sheet out from under it is locked
  // while that is true (TA-10731).
  const isBusy = items?.some((item) => item.isLoading) ?? false;

  const translateY = useSharedValue(0);

  const close = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const pan = Gesture.Pan()
    .enabled(!gestureDisabled && !showOverlay && !isBusy)
    .onUpdate(({ translationY }) => {
      if (translationY < 0) {
        // swiping up, so reduce the Y position match
        translateY.value = translationY / 10;
      } else {
        // swiping down, exact Y match
        translateY.value = translationY;
      }
    })
    .onEnd(({ translationY, velocityY }) => {
      const shouldGoBack =
        snapPoint(translationY, velocityY, [0, height]) === height;
      if (shouldGoBack) {
        // Animate the sheet out and pop only once it has settled, instead of
        // popping from inside onEnd. Unmounting a GestureDetector while its
        // gesture is still finishing can leave the native responder claimed
        // on iOS, which reads as "the screen stopped accepting taps until I
        // restarted the app" (TA-10731).
        translateY.value = withTiming(height, { duration: 150 }, (finished) => {
          if (finished) runOnJS(close)();
        });
      } else {
        translateY.value = withTiming(0);
      }
    });

  const style = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  const userAvatars = (users: User[]): AvatarProps[] => {
    return (
      users?.map((user) => {
        const { name, id, avatar } = user;
        return {
          id: id,
          label: name,
          avatar: avatar,
          size: 'small',
        };
      }) || []
    );
  };

  const paddingTop = insets.top < 45 ? 45 : insets.top;

  const isUploadShareModal = items?.some(
    (item) => item.title === 'File' || item.title === 'Gallery'
  );

  useEffect(() => {
    if (isUploadShareModal && isVisibleForChat && chatTutorialState === '3') {
      setShowOverlay(true);
      return;
    }
    setShowOverlay(false);
  }, [isUploadShareModal, isVisibleForChat, chatTutorialState]);

  const renderItem = (item: ModalItemType, index: number) => (
    <TouchableOpacity
      style={{
        paddingHorizontal: showOverlay ? theme.spacing.m : undefined,
      }}
      key={index}
      disabled={item.disable || showOverlay || isBusy}
      onPress={() => {
        if (!showHeader && closeOnTap) navigation.goBack();
        item.onSelect();
      }}
      accessibilityLabel={
        item.accessibilityLabel ? item.accessibilityLabel : item.title
      }>
      <Box
        flexDirection='row'
        paddingVertical='s'
        paddingHorizontal='xxs'
        opacity={item.disable || (isBusy && !item.isLoading) ? 0.5 : 1}
        alignItems='center'
        backgroundColor={showOverlay ? 'grey04' : undefined}>
        <Box flex={1} flexDirection='row'>
          <Box flex={1} flexDirection='row'>
            {item.isLoading ? (
              // Sits where the icon does, so the row keeps its height and the
              // label does not shift while the option is working.
              <Box
                width={theme.spacing.l}
                marginRight='m'
                alignItems='center'
                justifyContent='center'>
                <Spinner
                  testID={`modal-item-spinner-${index}`}
                  size={20}
                  strokeWidth={2}
                  color={theme.colors[item.color ?? 'textPrimary']}
                />
              </Box>
            ) : (
              item.iconName && (
                <Icon
                  name={item.iconName}
                  variant='l'
                  marginRight='m'
                  color={item.color || 'textPrimary'}
                />
              )
            )}
            <Text variant='body' color={item.color || 'textPrimary'}>
              {item.title}
            </Text>
          </Box>
          {item.label && (
            <Text
              variant='metadata'
              color='textSecondary'
              textTransform='capitalize'>
              {item.label}
            </Text>
          )}
          {item.comingSoon && (
            <Tag
              label={t('shared:comingSoon')}
              labelVariant='metadata'
              variant='secondary'
              padding='xxs'
              borderRadius='xxs'
            />
          )}
          {item.trailingIconName && (
            <Icon name='Check' color='greenBrand' variant='l' />
          )}
          {item.users && (
            <Box flexDirection='row'>
              <MemberListSummary
                isReadBy
                avatars={userAvatars(item.users)}
                onPress={() =>
                  navigation.navigate('chat-message-read-by-modal')
                }
                maxAvatars={3}
              />
            </Box>
          )}
        </Box>
      </Box>
    </TouchableOpacity>
  );

  const getMenus = (item: ModalItemType) => (
    <TouchableOpacity
      disabled={item.disable}
      onPress={() => {
        if (!showHeader && closeOnTap) navigation.goBack();
        showOverlay && setShowOverlay(false);
        item.onSelect();
      }}
      accessibilityLabel={
        item.accessibilityLabel ? item.accessibilityLabel : item.title
      }>
      <Box
        flexDirection='row'
        marginVertical='s'
        marginHorizontal='xxs'
        opacity={item.disable ? 0.5 : 1}
        alignItems='center'>
        <Box flex={1} flexDirection='row'>
          <Box flex={1} flexDirection='row'>
            {item.iconName && (
              <Icon
                name={item.iconName}
                variant='l'
                marginRight='m'
                color={item.color || 'textPrimary'}
              />
            )}
            <Text variant='body' color={item.color || 'textPrimary'}>
              {item.title}
            </Text>
          </Box>
          {item.label && (
            <Text
              variant='metadata'
              color='textSecondary'
              textTransform='capitalize'>
              {item.label}
            </Text>
          )}
        </Box>
      </Box>
    </TouchableOpacity>
  );

  const [menuHeight, setMenuHeight] = useState<number>();
  const [bottom, setBottom] = useState<number>();
  useEffect(() => {
    menuHeight &&
      menuHeight > 0 &&
      setBottom(
        menuHeight * 5 +
          theme.spacing.l +
          ((Platform.OS === 'ios' ? 50 : 15) *
            Dimensions.get('window').height) /
            (Platform.OS === 'ios' ? 844 : 800)
      );
  }, [menuHeight]);

  return (
    <Box
      style={{
        position: `absolute`,
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        justifyContent: 'flex-end',
        backgroundColor: 'transparent',
        paddingTop,
      }}>
      <TouchableWithoutFeedback
        disabled={showOverlay || isBusy}
        onPress={() => (goBack ? goBack() : navigation.goBack())}>
        <View
          style={{
            flex: 1,
            zIndex: 99,
            marginBottom: -10,
          }}></View>
      </TouchableWithoutFeedback>

      <GestureDetector gesture={pan}>
        <Animated.View style={style}>
          <Box
            style={[
              styles.belowModalWhiteSpace,
              { backgroundColor: showOverlay ? theme.colors.grey04 : 'white' },
            ]}
          />
          <ShadowBox
            variant='dark'
            borderTopLeftRadius='l'
            borderTopRightRadius='l'
            backgroundColor='white'
            paddingTop='m'
            paddingHorizontal={paddingDisabled || showOverlay ? undefined : 'm'}
            paddingVertical='l'
            style={{
              backgroundColor: showOverlay ? theme.colors.grey04 : 'white',
            }}
            onLayout={(event) => {
              const { height } = event.nativeEvent.layout;
              setHeight(height);
              setModalHeight(height);
            }}>
            <Box style={{ paddingBottom: insets.bottom }}>
              {Platform.OS !== 'web' && (
                <TouchableOpacity
                  disabled={showOverlay || isBusy}
                  onPress={() => (goBack ? goBack() : navigation.goBack())}>
                  <Box
                    backgroundColor='grey03'
                    height={4}
                    width={56}
                    borderRadius='xxxs'
                    alignItems='center'
                    alignSelf='center'
                    marginBottom='xs'
                  />
                </TouchableOpacity>
              )}
              {showHeader && (
                <Box
                  flexDirection='row'
                  marginTop='m'
                  marginBottom='xs'
                  justifyContent='space-between'>
                  <Box style={styles.back}>
                    <TouchableOpacity
                      onPress={() => (goBack ? goBack() : navigation.goBack())}>
                      <Text variant='buttonLabel' color='greenSecondary'>
                        {cancelLabel ?? t('shared:cancel')}
                      </Text>
                    </TouchableOpacity>
                  </Box>

                  <Box flex={1}>
                    <Text variant='labelEmphasized' textAlign='center'>
                      {headerTitle}
                    </Text>
                  </Box>

                  {showSaveButton && (
                    <Box style={styles.save}>
                      <TouchableOpacity
                        disabled={headerSaveDisable ?? false}
                        onPress={onSave}>
                        {!(headerSaveDisable ?? false) ? (
                          <Text color='greenSecondary'>
                            {t('models:chat.editTags.done')}
                          </Text>
                        ) : (
                          <Text color='grey04'>
                            {t('models:chat.editTags.done')}
                          </Text>
                        )}
                      </TouchableOpacity>
                    </Box>
                  )}
                </Box>
              )}
              <>
                {items && (
                  <FlatList
                    data={items}
                    scrollEnabled={false}
                    renderItem={({ item, index }) => {
                      if (
                        showOverlay &&
                        (item.title === 'File' || item.title === 'Gallery')
                      )
                        return (
                          <Box
                            onLayout={(e) => {
                              setMenuHeight(e.nativeEvent.layout.height);
                            }}
                            marginHorizontal='xs'
                            paddingHorizontal={
                              paddingDisabled ? undefined : 'xs'
                            }
                            borderTopLeftRadius={
                              item.title === 'Gallery' ? 'xs' : undefined
                            }
                            borderTopRightRadius={
                              item.title === 'Gallery' ? 'xs' : undefined
                            }
                            borderBottomLeftRadius={
                              item.title === 'File' ? 'xs' : undefined
                            }
                            borderBottomRightRadius={
                              item.title === 'File' ? 'xs' : undefined
                            }
                            backgroundColor='white'>
                            {getMenus(item)}
                          </Box>
                        );
                      return renderItem(item, index);
                    }}
                  />
                )}
              </>
              <Box style={[{ maxHeight: `100%` }]}>{children}</Box>
            </Box>
          </ShadowBox>
        </Animated.View>
      </GestureDetector>
      <ProjectOverlay
        visible={showOverlay && !!bottom}
        borderRadius='xs'
        pointerEvents='none'
        backgroundColor={theme.colors.transparent}
        children={
          <Box
            ml='m'
            alignSelf='flex-start'
            style={{ position: 'absolute', bottom: bottom }}>
            <TooltipPopup
              isFromChat
              visible={showOverlay}
              title='Upload & Share'
              description='Easily share photos or attach files via the Gallery and File options in the menu.'
              pointerPosition='bottom-left'
              currentStep={3}
              totalSteps={7}
            />
          </Box>
        }
      />
    </Box>
  );
};

const styles = StyleSheet.create({
  belowModalWhiteSpace: {
    ...StyleSheet.absoluteFillObject,
    top: `100%`,
    height: theme.spacing.listFooter,
    backgroundColor: theme.colors.white,
    marginTop: -theme.spacing.xs,
    zIndex: 2,
  },
  back: {
    zIndex: 99,
    position: 'absolute',
    left: 0,
  },
  save: {
    zIndex: 99,
    position: 'absolute',
    right: 0,
  },
  avatarContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'black',
  },
});

export default Modal;
